export default {
    title: "Email Integration",
    subtitle: "Connect your email accounts to automatically import documents from attachments",
    addAccount: "Add Email Account",

    // Empty state
    noAccounts: "No email accounts connected",
    noAccountsDescription: "Connect an email account to let Eve automatically import documents from your inbox.",
    getStarted: "Get Started",

    // Account list
    accountsList: {
        title: "Connected Accounts",
        lastSync: "Last sync",
        neverSynced: "Never synced",
        justNow: "Just now",
        minutesAgo_one: "{{count}} minute ago",
        minutesAgo_other: "{{count}} minutes ago",
        hoursAgo_one: "{{count}} hour ago",
        hoursAgo_other: "{{count}} hours ago",
        syncNow: "Sync Now",
        syncing: "Syncing...",
        toggleSync: "Toggle auto-sync",
        syncEnabled: "Auto-sync enabled",
        syncDisabled: "Auto-sync disabled",
        configRequired: "Configure sync settings to start",
        syncStarted: "Sync process started successfully",
        syncInProgress: "Sync in progress",
        syncingInfoTooltip: "Actions will be available again when sync finishes.",
        stopSync: "Stop sync",
    },

    // Status badges
    status: {
        active: "Connected",
        inactive: "Inactive",
        error: "Error",
        authentication_required: "Reconnect Required",
        syncing: "Syncing",
    },

    // Provider names
    providers: {
        gmail: "Gmail",
        outlook: "Outlook",
        imap_smtp: "IMAP/SMTP",
    },

    // Actions
    actions: {
        configure: "Configure",
        reconnect: "Reconnect",
        remove: "Remove",
        cancel: "Cancel",
        back: "Back",
        continue: "Continue",
        connect: "Connect Account",
        test: "Test Connection",
        save: "Save Changes",
        saving: "Saving...",
        close: "Close",
    },

    // Wizard
    wizard: {
        title: "Connect Email Account",
        steps: {
            email: "Email Address",
            connect: "Connect",
            success: "Complete",
        },
    },

    // Step 1: Email Entry
    emailEntry: {
        title: "Enter your email address",
        description: "We'll detect the best way to connect based on your email provider.",
        emailLabel: "Email Address",
        emailPlaceholder: "you@company.com",
        emailRequired: "Email address is required",
        emailInvalid: "Please enter a valid email address",
        displayNameLabel: "Display Name (optional)",
        displayNamePlaceholder: "e.g., Work Email, Documents Inbox",
        displayNameHint: "A friendly name to identify this account",
        providerDetected: "Provider detected: {{provider}}",
    },

    // Step 2A: Gmail OAuth
    gmailOAuth: {
        title: "Connect Gmail",
        description: "Sign in with your Google account to grant Eve read access to your emails.",
        secureAccess: "Secure Access with Google",
        permissionsTitle: "Eve will request permission to:",
        permissionRead: "Read your emails and attachments",
        permissionModify: "Mark emails as read after processing",
        signInButton: "Sign in with Google",
        redirectNote: "You will be redirected to Google's login page.",
    },

    // Step 2B: Outlook OAuth
    outlookOAuth: {
        title: "Connect Outlook",
        description: "Sign in with your Microsoft account to grant Eve read access to your emails.",
        secureAccess: "Secure Access with Microsoft",
        permissionsTitle: "Eve will request permission to:",
        permissionRead: "Read your emails and attachments",
        permissionModify: "Mark emails as read after processing",
        signInButton: "Sign in with Microsoft",
        redirectNote: "You will be redirected to Microsoft's login page.",
    },

    // Step 2C: IMAP Configuration
    imapConfig: {
        title: "Configure Email Server",
        description: "Enter your email server settings to connect your account.",
        presetLabel: "Email Provider",
        presetPlaceholder: "Select your email provider...",
        customOption: "Custom / Other Provider",

        imapSection: "Incoming Mail (IMAP)",
        imapHost: "IMAP Server",
        imapHostPlaceholder: "imap.example.com",
        imapHostRequired: "IMAP server is required",
        imapPort: "Port",
        imapUseSsl: "Use SSL/TLS",

        smtpSection: "Outgoing Mail (SMTP)",
        smtpHost: "SMTP Server",
        smtpHostPlaceholder: "smtp.example.com",
        smtpPort: "Port",
        smtpUseSsl: "Use SSL/TLS",

        credentialsSection: "Credentials",
        username: "Username",
        usernamePlaceholder: "Usually your email address",
        password: "Password / App Password",
        passwordPlaceholder: "Enter your password",
        passwordRequired: "Password is required",

        appPasswordHint: "If you have two-factor authentication enabled, you'll need to use an App Password instead of your regular password.",
        appPasswordLink: "Learn how to create an App Password",

        testConnection: "Test Connection",
        testing: "Testing connection...",
        testSuccess: "Connection successful!",
        testFailed: "Connection failed",
        testFailedMessage: "Could not connect: {{error}}",

        connecting: "Connecting...",
    },

    // Step 3: Success
    success: {
        title: "Account Connected!",
        description: "Your email account has been successfully connected to Eve.",
        whatHappensNext: "What happens next?",
        step1: "Eve will monitor your inbox for new emails with attachments",
        step2: "PDF and document attachments will be automatically imported",
        step3: "You'll find imported documents in the Documents section",
        viewAccounts: "View Connected Accounts",
        addAnother: "Add Another Account",
    },

    // Remove confirmation
    removeConfirm: {
        title: "Remove Email Account",
        message: "Are you sure you want to remove {{email}}? Eve will stop importing documents from this account.",
        confirm: "Remove Account",
        cancel: "Cancel",
    },

    // Stop sync confirmation
    stopSyncConfirm: {
        title: "Stop sync?",
        message: "The current sync may complete on the server. Auto-sync will be disabled and actions will be available again when the process finishes.",
        cancel: "Cancel",
        confirm: "Stop sync",
        stopping: "Stopping...",
    },

    // Configure modal
    configure: {
        title: "Configure Account",
        disclaimer: "This integration reads your emails silently, without changing the read/unread status of messages.",
        autoProcess: "Auto-process attachments",
        autoProcessHint: "Automatically create documents from email attachments",
        allowedDomains: "Allowed sender domains",
        allowedDomainsPlaceholder: "e.g., supplier.com, customer.com",
        allowedDomainsHint: "Only process emails from these domains. Leave empty to process all.",
        foldersToWatch: "Folders to watch",
        foldersToWatchHint: "Select which email folders to monitor for new emails",
        selectFolders: "Select folders...",
        loadingFolders: "Loading folders...",
        syncFromDate: "Sync emails from",
        syncFromDateHint: "Choose from which date to import emails. Applied on first sync.",
        syncFromNone: "New emails only (default)",
        syncFromToday: "From today",
        syncFrom7days: "Last 7 days",
        syncFrom30days: "Last 30 days",
        syncFromCustom: "Custom date",
        syncFromDateAlreadySynced: "This account has already been synced. The date only applies to the first sync.",
        syncConfirmTitle: "Sync emails?",
        syncConfirmMessage: "Do you want to proceed with email synchronization now?",
        syncNow: "Sync Now",
    },

    // Email view modal
    emailView: {
        title: "Email Details",
        subtitle: "View email content and attachments",
        from: "From",
        to: "To",
        subject: "Subject",
        date: "Date",
        body: "Content",
        attachments: "Attachments",
        noSubject: "(No subject)",
        noContent: "No content available for this email.",
        errorLoading: "Failed to load email details.",
        processed: "Processed",
    },

    // OAuth callbacks
    oauth: {
        success: "{{provider}} account connected successfully!",
        error: "Failed to connect {{provider}} account",
        errorGeneric: "Failed to connect email account. Please try again.",
    },

    // Errors
    errors: {
        loadAccounts: "Failed to load email accounts",
        createAccount: "Failed to create email account",
        updateAccount: "Failed to update email account",
        deleteAccount: "Failed to remove email account",
        testConnection: "Failed to test connection",
        oauthFailed: "OAuth authentication failed",
        toggleSync: "Failed to toggle sync status",
        syncFailed: "Sync failed",
    },
};
